import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, DownloadSimple, UsersFour } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusPill } from "../trainer/TrainerSchedule";
import CalendarSync from "../../components/CalendarSync";

export default function ClientSessions() {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [view, setView] = useState("upcoming");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { setSessions((await api.get("/sessions")).data); } catch (_e) {}
      try {
        const { data } = await api.get("/client/group/classes");
        if (data?.enabled) setClasses(data.classes || []);
      } catch (_e) {}
    })();
  }, []);

  const nowIso = new Date().toISOString();
  const groupItems = useMemo(() => classes.map((c) => ({
    id: `gc-${c.id}`, _group: true, _cls: c,
    start_time: new Date(c.start).toISOString(), duration_minutes: c.duration_min,
    session_type: c.title, location: c.location, status: "scheduled",
  })), [classes]);
  const upcoming = useMemo(
    () => [...sessions.filter((s) => s.status === "scheduled" && s.start_time >= nowIso), ...groupItems.filter((g) => g.start_time >= nowIso)]
      .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [sessions, groupItems, nowIso]
  );
  const archive = useMemo(
    () => sessions
      .filter((s) => !(s.status === "scheduled" && s.start_time >= nowIso))
      .sort((a, b) => b.start_time.localeCompare(a.start_time)),
    [sessions, nowIso]
  );
  const list = view === "upcoming" ? upcoming : archive;

  const downloadIcs = async (id) => {
    try {
      const res = await api.get(`/calendar/sessions/${id}.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `session-${id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="client-sessions">
      <div>
        <div className="tracker text-brand mb-3">[ Your training ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Your Sessions.</h1>
      </div>

      <div className="flex" data-testid="client-sessions-view-toggle">
        {[
          { key: "upcoming", label: "Upcoming", count: upcoming.length },
          { key: "archive", label: "Past", count: archive.length },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`tracker px-6 py-3 border ${view === v.key ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
            data-testid={`client-view-${v.key}`}
          >
            {v.label} ({v.count})
          </button>
        ))}
      </div>

      {view === "upcoming" && (
        <div className="card-flat p-4 flex items-center gap-3 text-sm text-zinc-400" data-testid="reminder-info">
          <Clock size={18} weight="duotone" className="text-brand" />
          <span>We'll send you a reminder email 24 hours before each scheduled session.</span>
        </div>
      )}

      <div className="space-y-6">
        {list.length === 0 ? (
          <div className="card-flat p-16 text-center text-zinc-500 tracker">
            {view === "upcoming" ? "No upcoming sessions" : "No past sessions yet"}
          </div>
        ) : (
          (() => {
            // Group sessions by date (toDateString as key, preserves order from list)
            const groups = [];
            const map = new Map();
            for (const s of list) {
              const key = new Date(s.start_time).toDateString();
              if (!map.has(key)) {
                map.set(key, { key, date: new Date(s.start_time), items: [] });
                groups.push(map.get(key));
              }
              map.get(key).items.push(s);
            }
            const todayKey = new Date().toDateString();
            return groups.map((g) => {
              const isToday = g.key === todayKey;
              return (
                <div key={g.key} data-testid={`day-group-${g.date.toISOString().slice(0,10)}`}>
                  <div className="flex items-baseline gap-3 mb-2 px-1">
                    <div className={`font-display text-2xl ${isToday ? "text-brand" : "text-white"}`}>
                      {g.date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                    </div>
                    {isToday && <span className="tracker text-brand-light text-[10px]">TODAY</span>}
                    <span className="tracker text-zinc-600">{g.items.length} session{g.items.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="card-flat">
                    {g.items.map((s) => {
                      const dt = new Date(s.start_time);
                      if (s._group) {
                        const c = s._cls;
                        const state = c.my_booking ? "booked" : c.my_waitlist ? "waitlisted" : c.spots_left === 0 ? "full" : "open";
                        return (
                          <div key={s.id} className="px-6 py-5 border-b border-white/5 last:border-b-0 flex items-center gap-6 hover:bg-[#1A1A1A] cursor-pointer" onClick={() => navigate("/client/group")} data-testid={`client-calendar-class-${c.id}`}>
                            <div className="w-20">
                              <div className="font-display text-3xl leading-none text-brand-light">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                              <div className="tracker mt-1 text-zinc-500">{c.duration_min} min</div>
                            </div>
                            <div className="flex-1">
                              <div className="font-display text-xl flex items-center gap-2"><UsersFour size={16} weight="duotone" className="text-brand-light" /> {c.title}</div>
                              <div className="tracker text-[10px] text-zinc-500 mt-0.5">
                                GROUP CLASS{c.instructor_name ? ` · with ${c.instructor_name}` : ""}{c.location ? ` · @ ${c.location}` : ""}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`tracker px-2 py-0.5 border text-[10px] ${state === "booked" ? "border-emerald-500/40 text-emerald-400" : state === "waitlisted" ? "border-amber-500/40 text-amber-400" : state === "full" ? "border-red-500/40 text-red-400" : "border-white/10 text-zinc-400"}`} data-testid={`client-calendar-class-state-${c.id}`}>
                                {state === "booked" ? (c.my_booking.status === "paid" ? "BOOKED · PAID" : "BOOKED") : state === "waitlisted" ? `WAITLIST #${c.my_waitlist.position}` : state === "full" ? `FULL${c.waitlist_count ? ` · ${c.waitlist_count} WAITING` : ""}` : `${c.spots_left} SPOT${c.spots_left === 1 ? "" : "S"} LEFT`}
                              </span>
                              <span className="btn-ghost !py-1.5 !px-3 !text-[10px]">{state === "open" ? "Book" : state === "full" ? "Waitlist" : "View"}</span>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={s.id} className="px-6 py-5 border-b border-white/5 last:border-b-0 flex items-center gap-6 hover:bg-[#1A1A1A]" data-testid={`client-session-${s.id}`}>
                          <div className="w-20">
                            <div className={`font-display text-3xl leading-none ${view === "archive" ? "text-zinc-500" : "text-brand"}`}>
                              {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                            <div className="tracker mt-1 text-zinc-500">{s.duration_minutes} min</div>
                          </div>
                          <div className="flex-1">
                            <div className="font-display text-xl">{s.session_type}</div>
                            {s.location && (
                              <div className="tracker text-[10px] text-brand-light mt-0.5" data-testid={`client-session-loc-${s.id}`}>
                                @ {s.location}
                              </div>
                            )}
                            {s.notes && <div className="text-xs text-zinc-500 mt-1">{s.notes}</div>}
                          </div>
                          <div className="flex items-center gap-2">
                            {s.reminder_sent_at && view === "upcoming" && (
                              <span className="tracker px-2 py-0.5 border border-brand/30 text-brand-light text-[10px]">REMINDED</span>
                            )}
                            <StatusPill status={s.status} />
                            {s.status === "scheduled" && view === "upcoming" && (
                              <button
                                onClick={() => downloadIcs(s.id)}
                                className="btn-ghost !py-1.5 !px-2 !text-[10px]"
                                data-testid={`client-ics-${s.id}`}
                                title="Add to calendar (.ics)"
                              >
                                <DownloadSimple size={14} weight="bold" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()
        )}
      </div>

      <CalendarSync />
    </div>
  );
}
